/// Tender handling — RYA Tender course + RYA Powerboat / Day Skipper
/// material. Five domains:
///
///   1. Tender types — inflatable, hard-bottomed, RIB, rowing.
///   2. Painters — single, double, bridled. Knots used.
///   3. Towing techniques — length, speed, conditions.
///   4. Boarding + disembarking — safety + weight distribution.
///   5. Davit + outboard operation.

// ───────────────────────────────────────────────────────────────────
// 1. Tender types
// ───────────────────────────────────────────────────────────────────

export type TenderType = "inflatable" | "rigid-inflatable" | "hard-bottomed" | "rowing";

export interface TenderInfo {
  id: TenderType;
  name: string;
  /// One-line gist.
  summary: string;
  /// Typical use case.
  bestFor: string;
  /// Pros + cons summary.
  proCons: string;
  /// Capacity + payload guidance.
  capacity: string;
}

export const TENDERS: readonly TenderInfo[] = Object.freeze([
  {
    id: "inflatable",
    name: "Soft inflatable (roll-up or slat-bottom)",
    summary:
      "All-inflatable tender — lightweight, deflates + rolls up for stowage on a yacht. Soft bottom (or slatted floor).",
    bestFor:
      "Small yachts where deck space is at a premium. Short trips to shore — fuel + crew + groceries.",
    proCons:
      "Pros: very light, easy stowage, low-pressure inflation. Cons: slow under oars or outboard, less stable in chop, bottom flex limits load capacity.",
    capacity:
      "2-4 adults max for typical 2.3-2.8 m inflatables. Payload ~200-300 kg.",
  },
  {
    id: "rigid-inflatable",
    name: "RIB (rigid hull, inflatable tubes)",
    summary:
      "Inflatable tubes around a rigid (fibreglass or aluminium) hull. The standard yacht-tender hull form for the last 30 years.",
    bestFor:
      "All-round yacht tender. Beach-launching, towing, fast transit to shore, two-person crew with shopping.",
    proCons:
      "Pros: fast on plane, stable, durable, good in chop. Cons: harder to stow (doesn't deflate to flat), heavier than soft inflatables.",
    capacity:
      "3-6 adults for typical 2.5-3.2 m RIBs. Payload 350-500 kg.",
  },
  {
    id: "hard-bottomed",
    name: "Hard-bottomed tender (fibreglass / wood / aluminium)",
    summary:
      "Traditional dinghy with no inflatable tubes — fibreglass, wood or aluminium hull.",
    bestFor:
      "Classic / wooden yachts; sailing tender; situations where puncture risk is high (reefs, rough beaches).",
    proCons:
      "Pros: cheaper, can be sailed, no puncture risk, lighter than RIB for given size. Cons: less stable than RIB, capsize risk if overloaded.",
    capacity:
      "2-4 adults. Payload limited by hull dimensions; check the manufacturer plate.",
  },
  {
    id: "rowing",
    name: "Rowing dinghy / pram",
    summary:
      "Small rowing tender, typically pram-bowed, with no engine. The classic tender of the wooden-boat era.",
    bestFor:
      "Sheltered moorings within rowing distance of shore; classic yachts; emergency backup tender.",
    proCons:
      "Pros: zero fuel, silent, simple. Cons: slow, limited range, capsizes if overloaded or in chop.",
    capacity:
      "2-3 adults max. Payload ~150-200 kg.",
  },
]);

// ───────────────────────────────────────────────────────────────────
// 2. Painters — towing lines + knots used
// ───────────────────────────────────────────────────────────────────

export type PainterStyle = "single" | "double" | "bridled";

export interface PainterSetup {
  id: PainterStyle;
  name: string;
  summary: string;
  whenToUse: string;
  /// Knots used at each end.
  knots: string;
  pitfalls: readonly string[];
}

export const PAINTERS: readonly PainterSetup[] = Object.freeze([
  {
    id: "single",
    name: "Single painter",
    summary:
      "One line from the tender's bow eye to the mother boat's stern cleat. Simplest setup.",
    whenToUse:
      "Short tows in benign conditions; tender follows the mother boat under control.",
    knots:
      "Tender end: bowline through the bow eye. Boat end: round turn + two half-hitches on a cleat OR cleat-hitch.",
    pitfalls: [
      "Painter too short — tender bow hits the mother boat's wake + crashes against the transom.",
      "Single point of attachment — if it fails, the tender drifts off without warning.",
      "Wrong knot — slip knots come undone; only use a bowline at the tender end.",
    ],
  },
  {
    id: "double",
    name: "Double painter (two lines)",
    summary:
      "Two painters in parallel — one as the primary, one as a backup.",
    whenToUse:
      "Anywhere the consequences of losing the tender are serious — open passages, long tows, when the tender contains expensive gear.",
    knots:
      "Each line: bowline at the tender end + cleat-hitch on the mother boat. Different cleats for each line.",
    pitfalls: [
      "Both painters on the same cleat — defeats the redundancy. Use different cleats.",
      "Lines of different length — one takes all the load; share the load by matching lengths.",
    ],
  },
  {
    id: "bridled",
    name: "Bridled painter",
    summary:
      "Single painter rigged via a bridle attached to TWO bow points on the tender — distributes the towing load + keeps the tender bow centred on its line.",
    whenToUse:
      "Towing at speed (planing speed for RIBs being towed by motorboats); rough conditions where the tender yaws if towed from one point.",
    knots:
      "Bridle: two bowlines or thimbles on the tender's bow attachment points; main painter knotted to the bridle midpoint OR a thimble.",
    pitfalls: [
      "Bridle lines unequal — tender pulls to one side + yaws.",
      "Bridle too short — restricts the tender's freedom to ride over waves.",
      "Bow attachment points not equally strong — failure at one rips the bridle off centre.",
    ],
  },
]);

// ───────────────────────────────────────────────────────────────────
// 3. Towing techniques
// ───────────────────────────────────────────────────────────────────

export interface TowingTechnique {
  id: "slow-tow" | "fast-tow" | "rough-water" | "downwind" | "marina";
  name: string;
  summary: string;
  speedKnots: string;
  painterLength: string;
  pitfalls: readonly string[];
}

export const TOWING_TECHNIQUES: readonly TowingTechnique[] = Object.freeze([
  {
    id: "slow-tow",
    name: "Slow tow (displacement speed)",
    summary:
      "Standard tow of a tender behind a yacht at displacement / cruising speed.",
    speedKnots: "4-7 kt — yacht's cruising speed under sail or engine.",
    painterLength:
      "Long enough that the tender sits IN ONE OF THE YACHT'S STERN WAVES — typically 2-3 boat lengths of yacht. The tender follows steadily, the bow rides up.",
    pitfalls: [
      "Painter too short — tender hits transom.",
      "Painter too long — tender drifts off line + can foul the propeller if tide / wind shifts.",
      "Tender drifting starboard or port — adjust the cleat attachment slightly to one side.",
    ],
  },
  {
    id: "fast-tow",
    name: "Fast tow (planing speed)",
    summary:
      "Towing a tender at planing speeds. Requires the tender to plane behind the mother boat.",
    speedKnots: "15+ kt for a planing tender. NOT possible with soft inflatables (will be dragged underwater).",
    painterLength: "Same length as the mother boat's hull — tender is pulled cleanly behind.",
    pitfalls: [
      "Soft inflatable on fast tow — drags under, fills with water, tears off the painter.",
      "Tender hitting wake — destabilises both boats; reduce speed.",
      "Outboard left tilted DOWN in the water at planing speed — drag enormous + outboard can pivot down + flip; tilt outboard UP for fast tow.",
    ],
  },
  {
    id: "rough-water",
    name: "Rough-water tow",
    summary:
      "Towing in chop or swell — painter management critical.",
    speedKnots: "Reduce to displacement speed (4-5 kt) regardless of mother boat's normal speed.",
    painterLength: "Adjust painter so the tender rides BETWEEN waves, not on the crests — tune by watching the tow.",
    pitfalls: [
      "Tender pitching dramatically — painter too short, tender bow stuffs.",
      "Painter taking sharp loads with each wave — protect with chafe gear at the cleat + at the tender bow eye.",
      "Submerging the tender's bow — sea fills the tender; drop tow speed immediately.",
    ],
  },
  {
    id: "downwind",
    name: "Downwind tow",
    summary:
      "Specific case where the tender is downwind of the mother boat — wash + swell behind the boat affect the tender.",
    speedKnots: "Normal cruising speeds; pay attention to following sea.",
    painterLength: "Slightly longer than normal to keep tender clear of the yacht's stern wave.",
    pitfalls: [
      "Tender 'surfing' down the yacht's stern wave + ramming the transom — increase painter length.",
      "Following sea breaking over tender's stern — bail before continuing.",
    ],
  },
  {
    id: "marina",
    name: "Marina manoeuvring (short tow)",
    summary:
      "Towing the tender while manoeuvring at slow speed in a marina or harbour.",
    speedKnots: "Idle / dead slow — 1-3 kt.",
    painterLength: "Very SHORT — tender held close to the yacht so it doesn't swing into berths, other boats, or buoys.",
    pitfalls: [
      "Tender swinging out + fouling other boats' lines.",
      "Tender drifting around the bow when manoeuvring in reverse — anticipate + steer accordingly.",
      "Tender's outboard or oars sticking out + catching cleats — secure them low.",
    ],
  },
]);

// ───────────────────────────────────────────────────────────────────
// 4. Boarding + disembarking
// ───────────────────────────────────────────────────────────────────

export const BOARDING_RULES: readonly string[] = Object.freeze([
  "ALWAYS wear a lifejacket — even for a 100 m row to shore.",
  "Board ONE PERSON AT A TIME. Don't overload the gunwale by jumping in.",
  "Step into the CENTRE of the tender, not the gunwale — centre weight stabilises; gunwale weight tips.",
  "Sit DOWN immediately on a thwart; don't stand or transfer cargo while still standing.",
  "Cargo first, then crew — bring shopping / fuel / water down BEFORE crew climb in.",
  "Heaviest item / person in the MIDDLE; never bow- or stern-heavy.",
  "From shore: step in OVER the gunwale (not on the gunwale); use a hand on the painter or yacht as support.",
  "Wear non-slip shoes when boarding from a wet dinghy at a slipway.",
  "If the tender is moving (wind, swell), have someone hold it AT THE GUNWALE — not at the painter.",
]);

// ───────────────────────────────────────────────────────────────────
// 5. Davit + outboard operation
// ───────────────────────────────────────────────────────────────────

export type DavitType = "single-arm" | "twin-arm" | "stern-platform";

export interface DavitInfo {
  id: DavitType;
  name: string;
  summary: string;
  whenToUse: string;
  procedure: readonly string[];
  pitfalls: readonly string[];
}

export const DAVITS: readonly DavitInfo[] = Object.freeze([
  {
    id: "single-arm",
    name: "Single-arm davit",
    summary:
      "Single curved arm extending over the side of the yacht; the tender hangs from a single tackle attached to a lifting eye.",
    whenToUse:
      "Smaller yachts that lift the tender clear of the water for passages. Common on motorboats.",
    procedure: [
      "Tilt outboard UP to clear the water.",
      "Attach the lifting tackle to the tender's central lifting eye.",
      "Operate the davit handle / winch to raise the tender clear of the water.",
      "Once clear, swing the davit inboard to bring the tender on deck or against the topside.",
      "Secure tender to the davit + the deck with additional lines so it can't swing in a roll.",
    ],
    pitfalls: [
      "Forgetting the outboard tilt — outboard drags + breaks the lifting eye.",
      "Davit handle un-stowed during sailing — gets caught by sheets.",
      "Tender swinging during lift — clear bystanders; control with a second crew on the deck.",
    ],
  },
  {
    id: "twin-arm",
    name: "Twin-arm davit",
    summary:
      "Two parallel arms with two tackles — tender lifts level + the bridge is stable.",
    whenToUse:
      "Larger yachts (40 ft+) and superyachts. The standard for cruising couples who lift the tender every night.",
    procedure: [
      "Tilt outboard UP.",
      "Attach tackles to two lifting points on the tender (bow + stern eyes typically).",
      "Operate both winches IN SYNC to lift the tender level.",
      "Lift clear of the water, then swing inboard.",
      "Secure tender to the davit + the deck.",
    ],
    pitfalls: [
      "Out-of-sync lifting — tender tilts + the outboard drags in the water.",
      "Bow-and-stern lifting points unequal — tender lists during lift.",
    ],
  },
  {
    id: "stern-platform",
    name: "Stern-platform tender storage",
    summary:
      "Tender carried on the swim platform / stern garage — no davit; the tender is winched onto the platform on rollers.",
    whenToUse:
      "Motoryachts + larger sailing yachts with integrated stern platforms or tender garages.",
    procedure: [
      "Lower the swim platform to water level (if it's a powered platform).",
      "Bring the tender alongside + drive / pull it onto the rollers.",
      "Strap the tender down to the platform using purpose-built strops.",
      "Raise the platform to its travel position (if powered).",
    ],
    pitfalls: [
      "Tender drives onto the platform crookedly — rights itself with mooring strops.",
      "Outboard catches on the platform rim when driving up — tilt outboard up first.",
    ],
  },
]);
